import type { SetTeamFreigabeInput, TeamFreigaben, UpsertTeamInput } from "@nl/shared";
import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  auswertungControllerBearbeitungsstatusOptions,
  auswertungControllerPruefungOptions,
  teamFreigabeControllerGetOptions,
  teamFreigabeControllerGetQueryKey,
  teamFreigabeControllerSetBereichMutation,
  teamFreigabeControllerSetEinheitMutation,
  teamsControllerCreateMutation,
  teamsControllerListQueryKey,
  teamsControllerRemoveMutation,
  teamsControllerSetMembersMutation,
  teamsControllerUpdateMutation,
  tenantsControllerAuditLogPageInfiniteQueryKey,
  tenantsControllerAuditLogQueryKey,
  tenantsControllerListQueryKey,
  usersControllerListQueryKey,
} from "@/client/@tanstack/react-query.gen";
import type { AppBereich, TrainingsEinheit } from "@nl/shared";

/**
 * Pilot groups: maintained by the platform in the tenant detail, read by a
 * coach for the own group. Kept apart from `queries.ts`, which is already
 * past the size limit.
 */

type Callbacks<T = unknown> = {
  onSuccess?: (data: T) => void;
  onError?: (error: unknown) => void;
};

// By operation id: these key functions need their parameters, and a key with
// parameters would miss every other variant of the query.
function invalidateByOperation(client: QueryClient, ...operations: string[]) {
  void client.invalidateQueries({
    predicate: (query) => {
      const head = query.queryKey[0] as { _id?: unknown } | undefined;
      return typeof head?._id === "string" && operations.includes(head._id);
    },
  });
}

/** The aggregates of a group follow its members and their roles. */
export function invalidateKennzahlen(client: QueryClient) {
  invalidateByOperation(
    client,
    "auswertungControllerPruefung",
    "auswertungControllerBearbeitungsstatus",
  );
}

/** The `mandant` side of a group's releases is the Sparkasse's state. */
export function invalidateGruppenFreigaben(client: QueryClient) {
  invalidateByOperation(client, "teamFreigabeControllerGet");
}

/**
 * Membership changes show up in the user table, in the counts of the tenant
 * list and in the aggregates of the groups.
 */
function useInvalidateGruppen() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: teamsControllerListQueryKey() });
    void client.invalidateQueries({ queryKey: usersControllerListQueryKey() });
    void client.invalidateQueries({ queryKey: tenantsControllerListQueryKey() });
    void client.invalidateQueries({ queryKey: tenantsControllerAuditLogQueryKey() });
    void client.invalidateQueries({
      queryKey: tenantsControllerAuditLogPageInfiniteQueryKey(),
    });
    invalidateKennzahlen(client);
  };
}

export function useSaveTeam(tenantId: string) {
  const invalidate = useInvalidateGruppen();
  const create = useMutation({ ...teamsControllerCreateMutation(), onSuccess: invalidate });
  const update = useMutation({ ...teamsControllerUpdateMutation(), onSuccess: invalidate });
  return {
    isPending: create.isPending || update.isPending,
    mutate: (teamId: string | undefined, body: UpsertTeamInput, options?: Callbacks) =>
      teamId
        ? update.mutate({ path: { id: teamId }, body }, options)
        : create.mutate({ path: { id: tenantId }, body }, options),
  };
}

export function useDeleteTeam() {
  const invalidate = useInvalidateGruppen();
  const base = useMutation({ ...teamsControllerRemoveMutation(), onSuccess: invalidate });
  return {
    isPending: base.isPending,
    mutate: (teamId: string, options?: Callbacks) =>
      base.mutate({ path: { id: teamId } }, options),
  };
}

export function useSetTeamMembers() {
  const invalidate = useInvalidateGruppen();
  const base = useMutation({ ...teamsControllerSetMembersMutation(), onSuccess: invalidate });
  return {
    isPending: base.isPending,
    mutate: (
      teamId: string,
      change: { add?: string[]; remove?: string[] },
      options?: Callbacks,
    ) =>
      base.mutate(
        { path: { id: teamId }, body: { add: change.add ?? [], remove: change.remove ?? [] } },
        options,
      ),
  };
}

export function useTeamFreigaben(tenantId: string, teamId: string) {
  return useQuery(
    teamFreigabeControllerGetOptions({ path: { id: tenantId, teamId } }),
  );
}

export function useSetTeamFreigabe(tenantId: string, teamId: string) {
  const client = useQueryClient();
  const queryKey = teamFreigabeControllerGetQueryKey({ path: { id: tenantId, teamId } });
  // The response is the new state, so the cache is set instead of refetched.
  const onSuccess = (data: TeamFreigaben) => {
    client.setQueryData(queryKey, data);
    void client.invalidateQueries({ queryKey: tenantsControllerAuditLogQueryKey() });
  };
  const bereich = useMutation({ ...teamFreigabeControllerSetBereichMutation(), onSuccess });
  const einheit = useMutation({ ...teamFreigabeControllerSetEinheitMutation(), onSuccess });
  return {
    isPending: bereich.isPending || einheit.isPending,
    setBereich: (b: AppBereich, body: SetTeamFreigabeInput, options?: Callbacks) =>
      bereich.mutate({ path: { id: tenantId, teamId, bereich: b }, body }, options),
    setEinheit: (e: TrainingsEinheit, body: SetTeamFreigabeInput, options?: Callbacks) =>
      einheit.mutate({ path: { id: tenantId, teamId, einheit: e }, body }, options),
  };
}

/** Aggregates of one group. A coach may only ask for the own one; the server answers 404 otherwise. */
export function useGruppenAuswertung(tenantId: string | undefined, teamId: string | undefined) {
  const query = { tenantId: tenantId ?? "", teamId };
  const enabled = Boolean(tenantId && teamId);
  return {
    pruefung: useQuery({ ...auswertungControllerPruefungOptions({ query }), enabled }),
    bearbeitung: useQuery({
      ...auswertungControllerBearbeitungsstatusOptions({ query }),
      enabled,
    }),
  };
}
